import { Button, Icon, Modal, Text } from "@shopify/polaris"
import { XIcon } from "@shopify/polaris-icons"
import React from "react"
import { Trans, useTranslation } from "react-i18next"
import { useIntercom } from "react-use-intercom"

import { CurrentSubscription, Plan } from "@/api/account/type.d"

import styles from "./index.module.scss"
import { useDowngradeModal } from "./useDowngradeModal"
import { TempPlanInfo } from "./utils"

// 重新导出TempPlanInfo类型供外部使用
export type { TempPlanInfo }

type DowngradeModalProps = {
  show: boolean
  isBundle?: boolean
  isAPPToBundle?: boolean
  currentSubscription: CurrentSubscription
  tempPlanInfo: TempPlanInfo
  currentPlan?: Plan
  onClose: () => void
  confirm: () => void
}

/**
 * 降级提醒弹窗
 * @constructor
 */
const DowngradeModal = (props: DowngradeModalProps) => {
  const { t } = useTranslation()
  const { show, onClose, confirm, currentSubscription, tempPlanInfo, currentPlan, isAPPToBundle } = props
  const { showNewMessage } = useIntercom()

  // 使用hook获取所有降级相关逻辑
  const {
    conditions,
    modalConfig,
    featureList,
    noticeText,
    getMainDescription,
  } = useDowngradeModal({
    currentSubscription,
    tempPlanInfo,
    currentPlan,
  })

  // 渲染重要提示组件
  const renderImportantNotice = () => {
    if (!conditions.shouldShowImportantNotice) return null

    return (
      <p className={"mt-3"}>
        <span className={"mr-1"}>
          <Text as="span" fontWeight="semibold">
            {t("account:Important")}
          </Text>
        </span>
        {noticeText}

        {conditions.isLowYearToHighMonth && (
          <p className={"mt-3"}>
            <Trans ns="account" i18nKey="InThisCase">
              <Button
                onClick={() => {
                  onClose()
                  showNewMessage()
                }}
                variant="plain"
              />
            </Trans>
          </p>
        )}
      </p>
    )
  }

  // 渲染功能特性列表
  const renderFeatureList = () => {
    if (!conditions.shouldShowFeatureList || featureList.length === 0) return null

    return (
      <div className={"mt-3 gap-x-4 gap-y-2 flex flex-wrap"}>
        {featureList.map((feature, index) => (
          <div key={index} className={styles.featureItem}>
            <div className={styles.featureItemIcon}>
              <Icon source={XIcon} />
            </div>
            <p>{feature}</p>
          </div>
        ))}
      </div>
    )
  }

  // 渲染升级建议
  const renderUpgradingSuggestion = () => (
    <div className={"mt-3 font-semibold"}>
      <Text as="span" fontWeight="semibold">
        {t("account:UpgradingSuggestion")}
      </Text>
    </div>
  )

  // 渲染高月费到低年费的Modal
  const renderHighMonthToLowerYearModal = () => (
    <Modal
      instant
      open={show}
      onClose={onClose}
      title={modalConfig.title}
      primaryAction={{
        content: modalConfig.primaryActionContent,
        destructive: true,
        onAction: confirm,
      }}
      secondaryActions={[
        {
          content: modalConfig.secondaryActionContent,
          onAction: onClose,
        },
      ]}
      sectioned
    >
      <p>{getMainDescription()}</p>
      {renderImportantNotice()}
      {renderFeatureList()}
      {renderUpgradingSuggestion()}
    </Modal>
  )

  // 渲染标准降级Modal
  const renderStandardDowngradeModal = () => (
    <Modal
      instant
      open={show}
      onClose={onClose}
      title={modalConfig.title}
      primaryAction={{
        content: modalConfig.primaryActionContent,
        destructive: true,
        onAction: confirm,
      }}
      secondaryActions={[
        {
          content: modalConfig.secondaryActionContent,
          onAction: onClose,
        },
      ]}
      sectioned
    >
      <p>
        <Trans
          ns="account"
          i18nKey="DowngradeReminderDesc"
          values={{ date: currentSubscription.billing_cycle_end_date }}
        >
          {/* @ts-ignore */}
          <Text as="span" fontWeight="semibold" />
        </Trans>
      </p>
      {renderImportantNotice()}
      {renderFeatureList()}
    </Modal>
  )

  // 根据条件返回对应的Modal, 高月切低级年费套餐，高月app套餐切bundle低级套餐，立即生效
  if (conditions.isHighMonthToLowerYear || isAPPToBundle && conditions.isDowngrade) {
    return renderHighMonthToLowerYearModal()
  }

  return renderStandardDowngradeModal()
}

export default DowngradeModal
